import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { detectAcademySlug } from '@/lib/academy';
import { fetchBranding, fetchBrandingById, type AcademyBranding } from '@/api/academy';

interface AcademyContextValue {
  slug: string | null;
  academyId: string | null;
  branding: AcademyBranding | null;
  isLoading: boolean;
  refreshBranding: () => Promise<void>;
}

const AcademyContext = createContext<AcademyContextValue>({
  slug: null,
  academyId: null,
  branding: null,
  isLoading: true,
  refreshBranding: async () => {},
});

export function AcademyProvider({ children }: { children: ReactNode }) {
  const [slug] = useState(() => detectAcademySlug());
  const [branding, setBranding] = useState<AcademyBranding | null>(() => {
    const stored = sessionStorage.getItem('academy_branding');
    if (stored) {
      try {
        const b = JSON.parse(stored) as AcademyBranding;
        // Keep academy_id in sync — it may be missing if the user has stale sessionStorage
        if (b.academy_id) sessionStorage.setItem('academy_id', b.academy_id);
        return b;
      } catch {
        return null;
      }
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(true);

  const loadBranding = useCallback(async (slugValue: string) => {
    const b = await fetchBranding(slugValue);
    setBranding(b);
    if (b.primary_color) {
      document.documentElement.style.setProperty('--color-primary', b.primary_color);
    }
    if (b.favicon_url) {
      const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (link) link.href = b.favicon_url;
    }
    sessionStorage.setItem('academy_id', b.academy_id);
    sessionStorage.setItem('academy_branding', JSON.stringify(b));
    return b;
  }, []);

  const loadBrandingById = useCallback(async (academyId: string) => {
    const b = await fetchBrandingById(academyId);
    setBranding(b);
    if (b.primary_color) {
      document.documentElement.style.setProperty('--color-primary', b.primary_color);
    }
    if (b.favicon_url) {
      const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (link) link.href = b.favicon_url;
    }
    sessionStorage.setItem('academy_id', b.academy_id);
    sessionStorage.setItem('academy_branding', JSON.stringify(b));
    return b;
  }, []);

  const refreshBranding = useCallback(async () => {
    const storedId = sessionStorage.getItem('academy_id');
    if (slug) {
      await loadBranding(slug);
    } else if (storedId) {
      await loadBrandingById(storedId);
    }
  }, [slug, loadBranding, loadBrandingById]);

  useEffect(() => {
    const storedId = sessionStorage.getItem('academy_id');
    const params = new URLSearchParams(window.location.search);
    const queryAcademyId = params.get('academy_id');

    if (slug) {
      loadBranding(slug)
        .catch(() => { /* academy not found — proceed without branding */ })
        .finally(() => setIsLoading(false));
    } else if (queryAcademyId) {
      loadBrandingById(queryAcademyId)
        .catch(() => { /* invalid academy_id — proceed without branding */ })
        .finally(() => setIsLoading(false));
    } else if (storedId) {
      loadBrandingById(storedId)
        .catch(() => {
          sessionStorage.removeItem('academy_id');
          sessionStorage.removeItem('academy_branding');
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [slug, loadBranding, loadBrandingById]);

  const academyId = branding?.academy_id ?? null;

  return (
    <AcademyContext.Provider value={{ slug, academyId, branding, isLoading, refreshBranding }}>
      {children}
    </AcademyContext.Provider>
  );
}

export function useAcademy() {
  return useContext(AcademyContext);
}
